import { create } from 'zustand';

const API_BASE = process.env.REACT_APP_API_URL || '';
const ICON_API_URL = API_BASE ? `${API_BASE}/public/configurator-icons` : null;

/**
 * IconStore — fetches and caches the configurator icon override map from the backend.
 *
 * The API returns a flat map like:
 *   { "dak/dak_option/Helder Glas": "https://...", ... }
 *
 * RadioWidget uses getIconUrl(tab, fieldName, optionValue) which:
 *   1. Returns the custom URL from this map if one exists
 *   2. Falls back to the static file path: icons/{tab}/{fieldName}-{optionValue}.png
 */
export const useIconStore = create((set, get) => ({
    iconMap: {},       // { "tab/field/value": "https://..." }
    loaded: false,
    loading: false,
    error: null,

    fetchIcons: async () => {
        if (get().loaded || get().loading) return;

        // Skip fetch if no API URL configured (local dev without backend)
        if (!ICON_API_URL) {
            set({ loaded: true });
            return;
        }

        set({ loading: true });
        try {
            const res = await fetch(ICON_API_URL);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            set({ iconMap: data || {}, loaded: true, loading: false });
        } catch (err) {
            console.warn('[IconStore] Could not load icon overrides, using static files.', err.message);
            set({ loaded: true, loading: false, error: err.message });
        }
    },

    /**
     * Get the icon URL for a specific tab/field/option.
     * Returns custom URL if available, else the static fallback path.
     */
    getIconUrl: (tab, fieldName, optionValue) => {
        const key = `${tab}/${fieldName}/${optionValue}`;
        const { iconMap } = get();
        if (iconMap[key]) return iconMap[key];
        // Static fallback — same path as before
        return `icons/${tab}/${fieldName}-${optionValue}.png`;
    },
}));
